import { PayloadAction } from '@reduxjs/toolkit';
import { MediaStreamKind, ParticipantSlot, SlotType } from '@yandex-video-platform/goloom-sdk';
import { batchActions } from 'redux-batched-actions';
import { AnyAction } from 'redux-saga';
import { all, call, put, select, takeLeading } from 'typed-redux-saga';

import { SELF } from '../../../../shared/constants';
import { goloomSdk } from '../../../../shared/features/goloom-sdk';
import { NOTIFICATIONS_MAP, notify } from '../../../../shared/features/notifications/ps-notifications';
import { getShowMyVideo } from '../../../../shared/features/settings';
import { isMobileUa } from '../../../../shared/features/uatraits/selectors';
import { ACTION, FEATURE } from '../../../../shared/helpers/logger-enums';
import { createLogger } from '../../../../shared/helpers/loggerFacade';
import { safe } from '../../../../shared/helpers/redux-saga';
import { GRID_VIEW } from '../../constants';
import { sagaErrorHandler } from '../../helpers/sagas-error-handlers';
import { sortParticipantsByDevices } from '../../helpers/sort-participants-by-devices';
import {
    canChangePage,
    getActiveGridView,
    getCurrentPage,
    getDesktopSharingStatus,
    getMaxDisplayParticipants,
    getParticipantByIds,
    getParticipants,
    getParticipantsCount,
    getScreenSharingParticipant,
    getUserPreferredGridView,
    selectIsNewGrid,
    selectIsSharingStarting,
    selectParticipants,
} from '../../selectors';
import { NotificationId } from '../../types';
import { selectSlots } from '../conference/selectors';
import { setParticipants, updateParticipant, updateSlots } from '../conference/slice';
import { setDesktopSharingStatus } from '../screen-sharing';
import { DesktopSharingStatus, unshareScreenAction } from '../screen-sharing/slice';
import {
    nextGridPageAction,
    prevGridPageAction,
    setCurrentGridPageAction,
    setCurrentPage,
    setStoreActiveGridView,
    setStoreUserPreferredGridView,
} from './slice';

const logger = createLogger('goloom-video-grid');

export function* getNextGridView(opts?: {
    userPreferredGridView?: GRID_VIEW;
    desktopSharingStatus?: DesktopSharingStatus;
}) {
    const activeGridView = yield* select(getActiveGridView);
    const userPreferredGridView = opts?.userPreferredGridView || (yield* select(getUserPreferredGridView));
    const desktopSharingStatus = opts?.desktopSharingStatus || (yield* select(getDesktopSharingStatus));
    const isNewGrid = yield* select(selectIsNewGrid);

    const nextGridView = desktopSharingStatus !== 'none' ? GRID_VIEW.PRESENTER : userPreferredGridView;

    if (nextGridView === activeGridView) {
        return;
    }

    const isMobile = yield* select(isMobileUa);
    return { gridView: nextGridView, isMobile, isNewGrid };
}

export function* setUserPreferredGridViewSaga({
    payload: { gridView: newGridView },
}: PayloadAction<{ gridView: GRID_VIEW }>) {
    const actionsToDispatch: AnyAction[] = [setStoreUserPreferredGridView({ gridView: newGridView })];
    const nextGridViewPayload = yield* call(getNextGridView, {
        userPreferredGridView: newGridView,
    });
    if (nextGridViewPayload) {
        actionsToDispatch.push(setStoreActiveGridView(nextGridViewPayload));
    }
    yield* put(batchActions(actionsToDispatch));
}

export function* slotsChangedSaga() {
    const oldSlots = yield* select(selectSlots);

    if (goloomSdk.session) {
        yield* put(
            updateSlots({
                prev: [...goloomSdk.session.slots.prev],
                current: [...goloomSdk.session.slots.current],
                next: [...goloomSdk.session.slots.next],
            })
        );
    }

    /// Проверяем наличие демонстрации
    const screenSharingParticipant = yield* select(getScreenSharingParticipant);

    //Отключаем шаринг если получается так, что мы шарим с кем-то
    const participants = yield* select(getParticipants);
    const participantsWithSharing = participants.filter((p) => p.desktopTrackState && !p.desktopTrackState.muted);
    if (participantsWithSharing.length > 1 && screenSharingParticipant?.id === SELF) {
        yield* put(notify(NOTIFICATIONS_MAP[NotificationId.SCREEN_SHARING_IS_ACTIVE]()));

        yield* put(unshareScreenAction());
        return;
    }
    const isMySharingStarting = yield* select(selectIsSharingStarting);
    const remoteDisplaySlot = goloomSdk.session?.slots.current.find(
        (slot) => slot.type === SlotType.PARTICIPANT && slot.mediaStreamKind === MediaStreamKind.DISPLAY
    ) as ParticipantSlot; // В эту переменную будет записываться слот с демонстрацией, (сам клиент с id SELF никогда туда не будет записан)
    const oldRemoteDisplaySlot = oldSlots.current.find(
        (slot) => slot.type === SlotType.PARTICIPANT && slot.mediaStreamKind === MediaStreamKind.DISPLAY
    );

    if (screenSharingParticipant?.id === SELF && !remoteDisplaySlot) {
        return;
    }

    // В goloomSdk.session.slots хранятся слоты участников, исключая текущего пользователя,
    // то есть там нет слота с камерой и слота с шарингом, которые транслируются от текущего пользователя
    if (remoteDisplaySlot) {
        const screenSharingUpdate = remoteDisplaySlot.participant?.userId !== oldRemoteDisplaySlot?.participant?.userId;
        if (screenSharingUpdate) {
            logger.startFeatureDelta({
                feature: FEATURE.SHARING,
                action: ACTION.WATCH,
                timeoutMS: 10_000,
            });
        }

        yield* call(setDesktopSharingStatus, 'remote');
        if (screenSharingParticipant?.id === SELF) {
            yield* put(
                updateParticipant({
                    id: SELF,
                    desktop: {
                        id: '',
                        muted: true,
                    },
                })
            );
            yield* put(unshareScreenAction());
        }
        if (remoteDisplaySlot.participant) {
            yield* put(
                updateParticipant({
                    id: remoteDisplaySlot.participant.userId,
                    desktop: {
                        id: '',
                        muted: false,
                    },
                })
            );
        }
    } else {
        if (isMySharingStarting) {
            yield* call(setDesktopSharingStatus, 'local');
            yield* put(
                updateParticipant({
                    id: SELF,
                    desktop: {
                        id: '',
                        muted: false,
                    },
                })
            );
        } else {
            yield* call(setDesktopSharingStatus, 'none');
            yield* put(
                updateParticipant({
                    id: SELF,
                    desktop: {
                        id: '',
                        muted: true,
                    },
                })
            );
        }

        if (screenSharingParticipant) {
            yield* put(
                updateParticipant({
                    id: screenSharingParticipant?.id,
                    desktop: {
                        id: '',
                        muted: true,
                    },
                })
            );
        }
    }
}

function* setCurrentGridPageSaga({ payload }: PayloadAction<number>) {
    const showMyVideo = yield* select(getShowMyVideo);
    const participantsCount = yield* select(getParticipantsCount);
    const maxDisplayParticipants = yield* select(getMaxDisplayParticipants);
    const participantsByIds = yield* select(getParticipantByIds);
    const participants = yield* select(selectParticipants);
    const currentPage = Math.min(Math.max(0, payload), Math.ceil(participantsCount / maxDisplayParticipants) - 1);

    yield* put(setCurrentPage(currentPage));
    yield* put(
        setParticipants(
            sortParticipantsByDevices({ showMyVideo, maxDisplayParticipants, participants, participantsByIds })
        )
    );
}

function* nextGridPageSaga() {
    const canNext = yield* select(canChangePage, 1);
    if (canNext) {
        const currentPage = yield* select(getCurrentPage);
        yield* put(setCurrentGridPageAction(currentPage + 1));
    }
}

function* prevGridPageSaga() {
    const canPrev = yield* select(canChangePage, -1);
    if (canPrev) {
        const currentPage = yield* select(getCurrentPage);
        yield* put(setCurrentGridPageAction(currentPage - 1));
    }
}
export function* videoGridSagaWatcher() {
    yield* all([
        takeLeading(setCurrentGridPageAction, safe(sagaErrorHandler('setCurrentGridPageSaga'), setCurrentGridPageSaga)),
        takeLeading(nextGridPageAction, safe(sagaErrorHandler('nextGridPageSaga'), nextGridPageSaga)),
        takeLeading(prevGridPageAction, safe(sagaErrorHandler('prevGridPageSaga'), prevGridPageSaga)),
    ]);
}
